import { useWatch } from 'react-hook-form';
import { BasicInfo, DetailTab, ExchangeRateTab, OtherTab } from './tabs';
import { useCalculations, useFormFieldStates } from '../../hooks';
import { FormSchema, initialFormValues } from '../../schema';
import { calculateTienVnd } from '../../utils/calculations';
import { AritoFormV2 } from '@/components/custom/erp';
import { useAuth } from '@/contexts/auth-context';
import { transformFormData } from '../../utils';
import { useInputTableRows } from '@/hooks';
import BottomBar from './tabs/BottomBar';
import { FormMode } from '@/types/form';

interface FormDialogProps {
  formMode: FormMode;
  initialData?: BanHangRecord;
  onClose: () => void;
  onSubmit: (data: BanHangPayload) => void;
}

export default function FormDialog({ formMode, initialData, onClose, onSubmit }: FormDialogProps) {
  const { entityUnit } = useAuth();
  const {
    rows,
    selectedRowUuid,
    handleRowClick,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    handleCellValueChange
  } = useInputTableRows<BanHangRecord>(initialData?.chi_tiet_data || []);

  const formFieldStates = useFormFieldStates(initialData);

  const handleSubmit = (data: BanHangPayload) => {
    // Calculate total amount using the same logic as the bottom bar
    const ma_ngv = data.ma_ngv || '1';
    const totalAmount = rows.reduce((sum, row) => {
      const rowAmount = calculateTienVnd(row, ma_ngv);
      return sum + rowAmount;
    }, 0);

    const formData = transformFormData(data, formFieldStates, rows, totalAmount, entityUnit, formMode);

    onSubmit(formData);
  };

  return (
    <AritoFormV2
      schema={FormSchema}
      initialData={initialData || initialFormValues}
      onSubmit={handleSubmit}
      mode={formMode}
      title={formMode === 'add' ? 'Mới' : undefined}
      subTitle='Bút toán điều chỉnh giảm công nợ'
      onClose={onClose}
      headerFields={<BasicInfo formMode={formMode} formFieldStates={formFieldStates} initialData={initialData} />}
      bottomBar={<DynamicBottomBar rows={rows} />}
      tabs={[
        {
          id: '1',
          label: 'Chi tiết',
          component: (
            <DetailTab
              formMode={formMode}
              rows={rows}
              selectedRowUuid={selectedRowUuid}
              handleRowClick={handleRowClick}
              handleAddRow={handleAddRow}
              handleDeleteRow={handleDeleteRow}
              handleCopyRow={handleCopyRow}
              handlePasteRow={handlePasteRow}
              handleMoveRow={handleMoveRow}
              handleCellValueChange={handleCellValueChange}
            />
          )
        },
        {
          id: '2',
          label: 'Tỷ giá',
          component: <ExchangeRateTab formMode={formMode} />
        },
        {
          id: '3',
          label: 'Khác',
          component: <OtherTab formMode={formMode} />
        }
      ]}
    />
  );
}

// Component that can access form context for dynamic bottom bar
function DynamicBottomBar({ rows }: { rows: BanHangRecord[] }) {
  // Get current ma_ngv value from form context
  const [ma_ngv] = useWatch({ name: ['ma_ngv'] });
  const { totalAmount } = useCalculations(rows, ma_ngv || '1');

  return <BottomBar totalAmount={totalAmount} />;
}
